function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import React, { lazy, memo, Suspense, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import useStyles from 'isomorphic-style-loader/useStyles';
import AutoSizer from 'react-virtualized-auto-sizer';
import cn from 'classnames';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { formatTimestamp } from 'helpers/datetime';
import { useAction } from 'hooks';
import App from 'modules/App';
import * as actions from 'modules/PatientResults/actions';
import * as selectors from 'modules/PatientResults/selectors';
import { COLORS, TREND_CHART_MARGIN, TREND_CHART_BASE_THEME, TREND_CHART_LOADER_THEME, TREND_CHART_DISTANCE } from '../../constants';
import AdditionalCharts from '../AdditionalCharts';
import { getBorderColor, getPlainValuesColor, getIsPlainValuesMode, getClickToPointDistance } from './helpers';
import { useGlucoseTrendData, useScaleValues, useTicks } from './hooks';
import AxisLines from './AxisLines';
import Average from './Average';
import PointSymbol from './PointSymbol';
import CustomRangeDragButton from './CustomRangeDragButton';
import CustomRangeLabel from './CustomRangeLabel';
import CustomRangeSplitButton from './CustomRangeSplitButton';
import StandardDeviation from './StandardDeviation';
import TargetZone from './TargetZone';
import Tooltip from './Tooltip';
import styles from './GlucoseConcentrationTrend.pcss';
const ResponsiveLine = /*#__PURE__*/ lazy(()=>import('@nivo/line').then((m)=>({
            default: m.ResponsiveLine
        })));
const GlucoseConcentrationTrend = ({ readings, averageData, dataType, start, end, ticks, criticalLow = 0, criticalHigh = 0, unitSymbol = null, gridTheme = null, isInProgress = false, isCustomRanges = false, isLineEnabled = false, isGridXEnabled = false, isReadOnly = false, isTooltipDisabled = false, showRelatedData = false, formatLabel, renderTick, addNote, updatePhiSet })=>{
    useStyles(styles);
    const activeReading = useSelector(selectors.selectedReading);
    const calculationFormula = useSelector(selectors.calculationFormula);
    const direction = useSelector(App.selectors.direction);
    const printMode = useSelector(App.selectors.printMode);
    const chartRef = useRef();
    const customRangeKeysRef = useRef([]);
    const relatedDataTimeRef = useRef();
    const timeSelectorRef = useRef();
    const valueSelectorRef = useRef();
    const chartLayoutRef = useRef();
    const setSelectedReading = useAction(actions.setSelectedReading);
    const [activePointTS, setActivePointTS] = useState(activeReading ? activeReading.timestamp : null);
    const [isChartLayoutReady, setChartLayoutReady] = useState(false);
    const { firstTick, lastTick, tickValues, tickParts } = useTicks(ticks, start, end);
    const trendData = useGlucoseTrendData(dataType, readings, averageData, tickValues, isInProgress);
    const { points, standardDeviations } = trendData;
    const { scaleValues, minScaleValue, maxScaleValue } = useScaleValues(trendData);
    useEffect(()=>{
        customRangeKeysRef.current = [];
    }, [
        tickValues.length
    ]);
    // Flips post-commit, not inside captureChartLayout's render-phase nivo layer call.
    useEffect(()=>{
        if (chartLayoutRef.current && !isChartLayoutReady) {
            setChartLayoutReady(true);
        }
    });
    if (points === null) {
        return null;
    }
    const allDataLength = points[0].data.length;
    const isPlainValuesMode = getIsPlainValuesMode(calculationFormula);
    const lineColor = isInProgress ? COLORS.placeholder : COLORS.line;
    const margin = TREND_CHART_MARGIN;
    const theme = _object_spread({}, TREND_CHART_BASE_THEME, isInProgress ? TREND_CHART_LOADER_THEME : null);
    const dataLayerColors = dataType === 'BGM' ? [
        'transparent',
        'transparent',
        'transparent',
        lineColor
    ] : [
        COLORS.high,
        COLORS.target,
        COLORS.low,
        'transparent'
    ];
    const axisY = {
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        tickValues: scaleValues
    };
    const format = (value)=>formatLabel ? formatLabel(value, firstTick, lastTick) : formatTimestamp(value);
    const getPointColor = (serie)=>{
        if (isInProgress) {
            return COLORS.placeholder;
        }
        if (serie.id === 'high') {
            return COLORS.high;
        }
        if (serie.id === 'low') {
            return COLORS.low;
        }
        if (serie.id === 'target') {
            return COLORS.target;
        }
        if (serie.id === 'averageHigh') {
            return COLORS.high;
        }
        if (serie.id === 'averageLow') {
            return COLORS.low;
        }
        if (serie.id === 'averageTarget') {
            return COLORS.target;
        }
        return isPlainValuesMode ? getPlainValuesColor(calculationFormula) : 'transparent';
    };
    const onAddNote = (point, evt)=>{
        if (!addNote) {
            return;
        }
        evt.preventDefault();
        evt.stopPropagation();
        const timestamp = point.data.x;
        const value = point.data.y;
        const originalTimestamp = get(trendData.originalTimestamps, timestamp, timestamp);
        const noteUnitSymbol = isPlainValuesMode ? unitSymbol : trendData.unitSymbol;
        const fullUnitSymbol = noteUnitSymbol && ` ${noteUnitSymbol}`;
        const standardDeviation = get(trendData.standardDeviations, timestamp);
        let status;
        if (trendData.levelTimestamps.highReadings.includes(timestamp)) status = 'high';
        else if (trendData.levelTimestamps.targetReadings.includes(timestamp)) status = 'target';
        else if (trendData.levelTimestamps.lowReadings.includes(timestamp)) status = 'low';
        addNote(originalTimestamp, value, standardDeviation, fullUnitSymbol, status);
    };
    const onMouseLeaveChart = ()=>{
        if (isReadOnly) {
            return;
        }
        if (valueSelectorRef.current) {
            valueSelectorRef.current.style.display = 'none';
        }
        if (timeSelectorRef.current) {
            timeSelectorRef.current.style.display = 'none';
        }
        if (relatedDataTimeRef.current) {
            relatedDataTimeRef.current.style.display = 'none';
        }
    };
    const onMouseMoveChart = (evt, chartMargin = margin, isRelatedData = false)=>{
        if (isReadOnly) {
            return;
        }
        const bounds = evt.currentTarget.getBoundingClientRect();
        const x = evt.clientX - bounds.left;
        const y = evt.clientY - bounds.top;
        if (y < margin.top || y > bounds.height - chartMargin.bottom || x < chartMargin.left || x > bounds.width - chartMargin.right) {
            if (valueSelectorRef.current) {
                valueSelectorRef.current.style.display = 'none';
            }
            if (timeSelectorRef.current) {
                timeSelectorRef.current.style.display = 'none';
            }
            if (relatedDataTimeRef.current) {
                relatedDataTimeRef.current.style.display = 'none';
            }
        } else {
            if (timeSelectorRef.current) {
                timeSelectorRef.current.style.display = 'block';
                timeSelectorRef.current.style.transform = `translate(${x}px)`;
            }
            if (relatedDataTimeRef.current) {
                relatedDataTimeRef.current.style.display = 'block';
                relatedDataTimeRef.current.style.transform = `translate(${x + 20}px)`;
            }
            if (!isRelatedData && valueSelectorRef.current) {
                valueSelectorRef.current.style.display = 'flex';
                valueSelectorRef.current.style.transform = `translate(0, ${y}px)`;
                const selectorValue = (maxScaleValue - minScaleValue) * (1 - (y - margin.top) / (bounds.height - chartMargin.bottom - chartMargin.top)) + minScaleValue;
                valueSelectorRef.current.innerHTML = selectorValue.toFixed(0);
            }
        }
    };
    const onMouseMove = (point, evt)=>{
        if (isReadOnly) {
            return;
        }
        const distance = getClickToPointDistance(point, evt);
        if (distance < TREND_CHART_DISTANCE) {
            const pointTS = point.data.x;
            if (chartRef.current.style.cursor !== 'pointer') {
                chartRef.current.style.cursor = 'pointer';
            }
            if (pointTS !== activePointTS) {
                setActivePointTS(pointTS);
            }
        } else if (chartRef.current.style.cursor !== 'auto' && distance >= TREND_CHART_DISTANCE) {
            chartRef.current.style.cursor = 'auto';
            if (activePointTS) {
                setActivePointTS(null);
            }
        }
    };
    const onClick = (point, evt)=>{
        const distance = getClickToPointDistance(point, evt);
        if (distance < TREND_CHART_DISTANCE) {
            onAddNote(point, evt);
            const reading = readings.find((r)=>r.timestamp === point.data.x);
            if (reading) {
                setSelectedReading(reading);
            }
        } else {
            setSelectedReading(null);
        }
    };
    const renderPoint = (pointSymbolProps)=>{
        if (pointSymbolProps.color === 'transparent') {
            return null;
        }
        return /*#__PURE__*/ _jsx(PointSymbol, _object_spread_props(_object_spread({}, pointSymbolProps), {
            activePointTS: activePointTS,
            trendData: trendData
        }));
    };
    const renderTooltip = (pointTooltipProps)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({}, pointTooltipProps), {
            trendData: trendData,
            isInProgress: isInProgress,
            isTooltipDisabled: isTooltipDisabled
        }));
    const renderAxisLines = (customLayerProps)=>/*#__PURE__*/ _jsx(AxisLines, _object_spread({}, customLayerProps));
    const renderAverage = (serieId, customLayerProps)=>{
        const { data } = customLayerProps;
        const dataSerie = data.find((series)=>series.id === serieId);
        if (!dataSerie || !dataSerie.data.length) {
            return null;
        }
        const key = `avg-${serieId}`;
        return /*#__PURE__*/ _createElement(Average, _object_spread_props(_object_spread({}, customLayerProps), {
            key: key,
            dataSerie: dataSerie,
            tickParts: tickParts,
            getPointColor: getPointColor,
            getBorderColor: getBorderColor
        }));
    };
    const renderAverages = (customLayerProps)=>{
        const averageSerieIds = [
            'averageHigh',
            'averageTarget',
            'averageLow'
        ];
        return /*#__PURE__*/ _jsx("g", {
            children: averageSerieIds.map((serieId)=>renderAverage(serieId, customLayerProps))
        });
    };
    // Report/read-only rendering doesn't use nivo layers (see renderReadOnlyCustomRange*
    // below), so this only ever runs for the interactive, editable chart.
    const renderCustomRangeDragButtons = (customLayerProps)=>{
        if (!isCustomRanges || isReadOnly) {
            return null;
        }
        const buttons = [];
        const keys = customRangeKeysRef.current;
        const len = tickValues.length - 1;
        for(let idx = 0; idx < len; idx++){
            if (!keys[idx]) {
                keys[idx] = uuid();
            }
            if (idx) {
                buttons.push(/*#__PURE__*/ _createElement(CustomRangeDragButton, _object_spread_props(_object_spread({}, customLayerProps), {
                    key: `drag-${keys[idx]}`,
                    idx: idx,
                    tickValues: tickValues,
                    updatePhiSet: updatePhiSet
                })));
            }
            buttons.push(/*#__PURE__*/ _createElement(CustomRangeSplitButton, _object_spread_props(_object_spread({}, customLayerProps), {
                key: `split-${keys[idx]}`,
                idx: idx,
                tickValues: tickValues,
                updatePhiSet: updatePhiSet
            })));
        }
        customRangeKeysRef.current = keys;
        return buttons;
    };
    const renderCustomRangeLabels = (customLayerProps)=>{
        if (!isCustomRanges || isReadOnly) {
            return null;
        }
        const { innerWidth, innerHeight } = customLayerProps;
        const keys = customRangeKeysRef.current;
        const labels = [];
        for(let idx = 0; idx < tickValues.length - 1; idx++){
            labels.push(/*#__PURE__*/ _createElement(CustomRangeLabel, _object_spread_props(_object_spread({}, customLayerProps), {
                key: `label-${keys[idx]}`,
                idx: idx,
                tickValues: tickValues,
                isReadOnly: false,
                updatePhiSet: updatePhiSet
            })));
        }
        const style = {
            width: innerWidth,
            height: margin.bottom,
            position: 'relative',
            transform: `translate(0,${innerHeight}px)`
        };
        return /*#__PURE__*/ _jsx("foreignObject", {
            style: style,
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.customRange__labels,
                children: labels
            })
        });
    };
    const captureChartLayout = (customLayerProps)=>{
        const { xScale, innerWidth, innerHeight } = customLayerProps;
        chartLayoutRef.current = {
            xScale,
            innerWidth,
            innerHeight
        };
        return null;
    };
    const renderReadOnlyCustomRangeDividers = ()=>{
        if (!isCustomRanges || !isReadOnly || !chartLayoutRef.current) {
            return null;
        }
        const { xScale, innerWidth, innerHeight } = chartLayoutRef.current;
        const style = {
            position: 'absolute',
            left: margin.left,
            top: margin.top,
            width: innerWidth,
            height: innerHeight
        };
        return /*#__PURE__*/ _jsx("div", {
            className: styles.customRange__linesWrapper,
            style: style,
            children: tickValues.slice(1, -1).map((tick)=>/*#__PURE__*/ _jsx("div", {
                    className: styles.customRange__line,
                    style: {
                        left: xScale(tick)
                    }
                }, tick))
        });
    };
    const renderReadOnlyCustomRangeLabels = ()=>{
        if (!isCustomRanges || !isReadOnly || !chartLayoutRef.current) {
            return null;
        }
        const { xScale, innerWidth, innerHeight } = chartLayoutRef.current;
        const labels = [];
        for(let idx = 0; idx < tickValues.length - 1; idx++){
            labels.push(/*#__PURE__*/ _jsx(CustomRangeLabel, {
                xScale: xScale,
                idx: idx,
                tickValues: tickValues,
                isReadOnly: true,
                updatePhiSet: updatePhiSet
            }, `label-${idx}`));
        }
        const style = {
            position: 'absolute',
            left: margin.left,
            top: innerHeight + margin.top,
            width: innerWidth,
            height: margin.bottom
        };
        return /*#__PURE__*/ _jsx("div", {
            style: style,
            children: /*#__PURE__*/ _jsx("div", {
                className: cn(styles.customRange__labels, styles['customRange__labels--readOnly']),
                children: labels
            })
        });
    };
    const renderStandardDeviations = (customLayerProps)=>{
        if (isEmpty(standardDeviations)) {
            return null;
        }
        const { data } = customLayerProps;
        return flatten(data.map((dataSerie)=>{
            if (dataSerie.id === 'all') return null;
            return dataSerie.data.map((point)=>/*#__PURE__*/ _jsx(StandardDeviation, _object_spread_props(_object_spread({}, customLayerProps), {
                    point: point,
                    dataSerie: dataSerie,
                    trendData: trendData,
                    getPointColor: getPointColor
                })));
        }));
    };
    const renderTargetZone = (customLayerProps)=>/*#__PURE__*/ _jsx(TargetZone, _object_spread_props(_object_spread({}, customLayerProps), {
            criticalLow: criticalLow,
            criticalHigh: criticalHigh,
            isInProgress: isInProgress
        }));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: cn('nivoChart', styles.root, {
                    [styles['root--readOnly']]: isReadOnly
                }),
                children: /*#__PURE__*/ _jsx(AutoSizer, {
                    disableHeight: true,
                    disableWidth: printMode,
                    children: ({ width })=>{
                        const xGap = width && (width - margin.right - margin.left) / (tickValues.length - 1);
                        return /*#__PURE__*/ _jsxs("div", {
                            className: cn('nivoChart__inner', styles.root__inner, {
                                fadingLoader: isInProgress,
                                [styles['root__inner--activeNoting']]: addNote
                            }),
                            ref: chartRef,
                            onMouseLeave: onMouseLeaveChart,
                            onMouseMove: onMouseMoveChart,
                            children: [
                                /*#__PURE__*/ _jsx(Suspense, {
                                    fallback: null,
                                    children: /*#__PURE__*/ _jsx(ResponsiveLine, {
                                        data: [
                                            ...points
                                        ],
                                        animate: !printMode,
                                        margin: margin,
                                        xScale: {
                                            type: 'linear',
                                            min: firstTick,
                                            max: lastTick,
                                            reverse: direction === 'rtl'
                                        },
                                        yScale: {
                                            type: 'linear',
                                            min: minScaleValue,
                                            max: maxScaleValue
                                        },
                                        axisLeft: direction === 'rtl' ? null : axisY,
                                        axisRight: direction === 'ltr' ? null : axisY,
                                        axisTop: null,
                                        axisBottom: {
                                            tickSize: 0,
                                            tickPadding: 10,
                                            tickRotation: 0,
                                            tickValues,
                                            format,
                                            renderTick: renderTick && ((tickProps)=>renderTick(tickProps, xGap))
                                        },
                                        enableGridX: isGridXEnabled,
                                        enableGridY: false,
                                        gridXValues: tickValues,
                                        gridYValues: scaleValues,
                                        colors: dataLayerColors,
                                        curve: dataType === 'CGM' ? 'monotoneX' : 'linear',
                                        theme: _object_spread({}, theme, gridTheme),
                                        motionConfig: "stiff",
                                        enableArea: true,
                                        enablePoints: dataType !== 'CGM',
                                        areaOpacity: 0.1,
                                        lineWidth: 1,
                                        pointSize: 9,
                                        pointBorderColor: getBorderColor,
                                        pointSymbol: renderPoint,
                                        pointColor: getPointColor,
                                        pointBorderWidth: 0,
                                        crosshairType: direction === 'ltr' ? 'bottom-left' : 'bottom-right',
                                        useMesh: true,
                                        enableCrosshair: false,
                                        tooltip: renderTooltip,
                                        layers: [
                                            'grid',
                                            'markers',
                                            'axes',
                                            // (isLineEnabled ? 'areas' : null),
                                            isPlainValuesMode ? null : renderTargetZone,
                                            isInProgress ? null : 'crosshair',
                                            isLineEnabled ? 'lines' : null,
                                            'points',
                                            'slices',
                                            allDataLength ? 'mesh' : null,
                                            'legends',
                                            renderStandardDeviations,
                                            renderAxisLines,
                                            renderAverages,
                                            renderCustomRangeDragButtons,
                                            renderCustomRangeLabels,
                                            captureChartLayout
                                        ],
                                        onMouseMove: onMouseMove,
                                        onClick: onClick,
                                        isInteractive: !isReadOnly
                                    })
                                }),
                                isReadOnly ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        renderReadOnlyCustomRangeDividers(),
                                        renderReadOnlyCustomRangeLabels()
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        showRelatedData && /*#__PURE__*/ _jsx("div", {
                                            ref: timeSelectorRef,
                                            className: styles.timeSelector__chart
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            ref: valueSelectorRef,
                                            className: styles.valueSelector
                                        })
                                    ]
                                })
                            ]
                        });
                    }
                })
            }),
            showRelatedData && !isReadOnly && /*#__PURE__*/ _jsx(AdditionalCharts, {
                firstTick: firstTick,
                lastTick: lastTick,
                relatedDataTimeRef: relatedDataTimeRef,
                isTooltipDisabled: isTooltipDisabled,
                onMouseMoveChart: onMouseMoveChart,
                onMouseLeaveChart: onMouseLeaveChart
            })
        ]
    });
};
export default /*#__PURE__*/ memo(GlucoseConcentrationTrend);
